import { useAuth } from "@ozpanel/ui";
// The mail-activity shape lives once, in the shared kit, so the tenant and mailbox consoles read
// the same fields the same way (single-source-of-truth). Re-exported so this app's own imports of
// MailActivity keep working from "../api".
import type { MailActivity } from "@ozpanel/kit";
export type { MailActivity } from "@ozpanel/kit";

// api.ts — typed wrappers over the mailbox endpoints this app needs, each a thin call
// through AuthClient.apiFetch (envelope decoding, CSRF, credentials already handled there).
// Not generated (V6.2's typegen, D6, is still pending) — hand-written against the real Go
// handlers, verified directly (mail_me_diagnostics.go, mail_sso.go, mail_branding.go).

// The two clients mail_me_diagnostics.go's own webmailClients map validates against —
// mirrored here rather than fetched from an unverified catalogue endpoint (GET
// /webmail/clients' shape is now confirmed: it answers under `clients`.
export const KNOWN_CLIENTS = ["roundcube", "sogo"] as const;
export type WebmailClient = (typeof KNOWN_CLIENTS)[number];

export interface WebmailDefault {
  default: WebmailClient;
}

export interface SoundPref {
  effective: string;
  own?: string;
}

export interface SSOResult {
  /** The best place to go — absolute when the server could resolve the domain's exposure. */
  url: string;
  /**
   * Every absolute URL this mailbox's webmail is actually reachable at, host form first.
   *
   * The server builds these from the domain's own exposure row. The client must not construct
   * them: it used to join a relative "/{client}/" onto each of two guessed origins, which is
   * correct for the HOST exposure and a 404 for the PATH one, because there the prefix IS the
   * mount and the client name does not belong in it.
   */
  urls?: string[];
  client: string;
  expires_in?: number;
}

// Everything v1's mailbox.html does, typed. v1 is the specification here: it is complete,
// in production, and its request shapes are the ones the Go handlers actually accept — which
// this repository has been bitten by more than once (an alias posted as `source` when the
// handler reads `localpart`, a db user posted as `name` when it reads `label`). These were read
// off web/panel/mailbox.html's own calls rather than guessed from endpoint names.

export interface MeMailbox {
  address: string;
  domain: string;
  localpart: string;
  quota_mb: number;
  /**
   * The server sends `status` — "active" or a suspension state — and never `active`.
   *
   * This was declared `active: boolean`, and the Overview rendered it directly as the "Receiving
   * mail" fact with `goodWhen: true`. `undefined` is not `true`, so every mailbox user was shown
   * their mail as NOT being received, on a working mailbox, with no error anywhere. Found by the
   * response-shape sweep against node10, 2026-08-31, where the real body reads
   * {localpart, domain, address, quota_mb, status:"active"}.
   *
   * mail_me.go treats anything other than "active" as suspended (it refuses with 409 "your
   * mailbox is suspended"), so that is the comparison the console makes too rather than
   * enumerating states it would have to keep in step.
   */
  status: string;
}
export interface Me {
  mailbox: MeMailbox;
  usage: { used_mb: number | null; quota_mb: number; limit_mb?: number | null; limit_from?: string; limit_note?: string; percent: number | null; unknown?: string }; // null: not measured; limit_mb null = unlimited
  app_passwords: { count: number | null; max: number }; // count null: could not be counted
}
export interface Filter {
  name: string;
  field: string;
  op: string;
  value: string;
  action: string;
  target?: string;
  stop?: boolean;
  enabled?: boolean;
}
export interface Vacation {
  enabled: boolean;
  subject: string;
  message: string;
  start_date?: string | null;
  end_date?: string | null;
}
export interface SpamLevels {
  spam_score: number | null;
  reject_score: number | null;
}
export interface SpamView {
  address: string;
  mine: SpamLevels;
  domain: SpamLevels;
  platform: SpamLevels;
  note: string;
  held_now?: number;
  // What the filter actually DID: scanned / delivered / sent_to_junk / refused. v1 renders these
  // as the "What the filter did with your mail" figures — the numbers that answer "is filtering
  // even on", which no setting can.
  activity?: { scanned?: number; delivered?: number; sent_to_junk?: number; refused?: number; note?: string } | null;
}
export interface HeldMessage {
  id: string;
  sender: string;
  subject?: string;
  recipients?: string[];
  size_bytes?: number;
  // arrived_at, and it is UNIX SECONDS — not received_at, and not a string. Reading the wrong
  // name showed an empty column; reading it as a date string would show "Invalid Date".
  arrived_at?: number;
  reason?: string;
}
export interface AppPassword {
  id: number;
  name: string;
  // WHAT THE DEVICE SIGNS IN AS — localpart+devicetag@domain, not the plain address. The server
  // returns it on create and on every listing, and the console dropped it in both places: a user
  // was handed a password and left to guess a username that their own address is not.
  username?: string;
  device_tag?: string;
  created_at: string;
  last_used_at?: string | null;
}
export interface Device {
  service: string;
  ip: string;
  user_agent?: string;
  since?: string;
}
export interface Session {
  // A string. Declared as a number it typechecked and only worked by coercion when built into a
  // URL — the sort of thing that keeps working until an id stops looking numeric.
  id: number | string;
  user_agent: string;
  ip: string;
  created_at: string;
  // The server sends these and the type omitted them, so a session's freshness — the one field
  // that distinguishes a live sign-in from a stale row — could not be read at all.
  last_seen_at?: string;
  state?: string;
  current?: boolean;
}
// Two endpoints answer with this row and they name the title differently: /tokens/me sends
// `label` (accesstoken.Row) and /auth/tokens sends `name`. Both are carried rather than one being
// picked, because asserting either name alone leaves the other endpoint's rows unnamed.
// TWO interfaces, because these are two different rows from two different tables, and carrying
// them in one type is what broke revoke.
//
// The single ApiToken declared `id` as required and made `jti` optional, then the shared card
// revoked with `t.id`. /tokens/me rows have NO id — they are keyed on `jti`, which is what
// POST /tokens/me/{jti}/revoke takes — so every Revoke button on the Access tokens card posted
// to `/tokens/me/undefined/revoke`. It typechecked, because `id` was declared present on a row
// that never carries it. Found by the response-shape sweep as a MISSING field, 2026-08-31.
//
// The union also cost the Apple Mail profile: `jti` being optional is why it was dropped from
// the type in the first place, and the download link is keyed on it.

/** A panel API key, from GET /auth/tokens. Keyed on `id`. */
export interface ApiToken {
  // A string on /auth/tokens. Declared as a number it typechecked and then produced
  // `/auth/tokens/[object Number]`-shaped URLs only by luck of coercion.
  id: number | string;
  name?: string;
  created_at: string;
  last_used_at?: string | null;
  expires_at?: string | null;
  scopes?: string[] | string | null;
  allow_dangerous?: boolean;
}

/** A mailbox access token, from GET /tokens/me. Keyed on `jti` — it has no `id`. */
export interface PersonalToken {
  jti: string;
  label?: string;
  subject?: string;
  level?: string;
  scopes?: string[] | string | null;
  created_at: string;
  created_by?: string;
  last_used_at?: string | null;
  expires_at?: string | null;
  tenant_id?: number | null;
  // Present only on the create response, never on a list.
  token?: string;
  // Why a revoked token must not offer an Apple Mail profile: the profile embeds the token, so
  // handing one out for a dead credential installs an account that cannot sign in — and the
  // person blames the mail app, not the token.
  revoked_at?: string | null;
}
export interface PushSubscription {
  id: number;
  user_agent: string;
  created_at: string;
}

export function useMailboxApi() {
  const { client } = useAuth();
  const post = <T,>(p: string, body?: unknown) =>
    client.apiFetch<T>(p, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
  const put = <T,>(p: string, body: unknown) =>
    client.apiFetch<T>(p, { method: "PUT", body: JSON.stringify(body) });
  const del = <T,>(p: string) => client.apiFetch<T>(p, { method: "DELETE" });

  return {
    // ── At a glance, storage ────────────────────────────────────────────────────────────
    me: () => client.apiFetch<Me>("/mail/me"),
    // The mailbox dashboard: today + this-hour incoming/outgoing by outcome, the sending limit's
    // live usage, and the forwarders. Read once for the "at a glance" strip.
    activity: () => client.apiFetch<MailActivity>("/mail/me/activity"),
    // Typed, because it was Record<string, unknown> and so nothing in it could be rendered
    // without a cast — which is why an Overview sat beside a payload already carrying the
    // sending limit, its scope, the message counts and the signing state.
    diagnostics: () =>
      client.apiFetch<{
        address?: string;
        webmail_default?: string;
        health?: { active?: boolean; dkim_signing?: boolean | null; over_quota?: boolean | null };
        messages?: { inbox?: number; sent?: number };
        notifications?: { subscriptions?: number | null };
        storage?: { used_mb?: number; quota_mb?: number; percent?: number };
        limits?: {
          send_per_hour?: number;
          send_per_day?: number;
          send_scope?: string;
          send_scope_note?: string;
          max_message_mb?: number;
          max_attachment_mb?: number;
          attachment_note?: string;
        };
      }>("/mail/me/diagnostics"),

    // Your own send/receive statistics over time (mail_stats, Phase 4). by_direction splits what
    // you sent out from what was delivered to you; totals is per outcome.
    myStats: (days = 30) =>
      client.apiFetch<{
        mailbox?: string;
        totals?: Record<string, number>;
        by_direction?: { inbound?: number; outbound?: number };
        note?: string;
      }>(`/mail/me/stats?days=${days}`),

    // ── Mail password (distinct from the panel sign-in password) ────────────────────────
    setMailPassword: (current_password: string, new_password: string) =>
      post<unknown>("/mail/me/password", { current_password, new_password }),

    // ── Rules and away message ──────────────────────────────────────────────────────────
    filters: () => client.apiFetch<{ items: Filter[] | null; total: number }>("/mail/me/filters"),
    // A WHOLE-SET replace, as v1 does it. Sending one rule would delete the others.
    replaceFilters: (filters: Filter[]) => put<unknown>("/mail/me/filters", { filters }),
    vacation: () => client.apiFetch<Vacation>("/mail/me/vacation"),
    setVacation: (v: Vacation) => put<unknown>("/mail/me/vacation", v),

    // ── Spam: yours, your domain's, the platform's ──────────────────────────────────────
    spam: () => client.apiFetch<SpamView>("/mail/me/spam"),
    // null means "follow my domain" — a real value, not an absent field.
    setSpam: (spam_score: number | null, reject_score: number | null) =>
      put<unknown>("/mail/me/spam", { spam_score, reject_score }),

    // Your OWN allow/block senders, scoped to your mailbox — the fix for a sender that keeps being
    // held: add it once instead of releasing every message. The server forces the scope to you.
    spamLists: () =>
      client.apiFetch<{
        mailbox: string;
        allow: { id: number; value: string }[] | null;
        block: { id: number; value: string }[] | null;
        note?: string;
      }>("/mail/me/spam/lists"),
    addSpamSender: (list_type: "allow" | "block", value: string) =>
      post<unknown>("/mail/me/spam/lists", { list_type, value }),
    deleteSpamSender: (id: number) => del<unknown>(`/mail/me/spam/lists/${id}`),

    // Read-only scorecard for YOUR domain: is it set up so the internet accepts your mail
    // (MX/SPF/DKIM/DMARC/…)? You cannot change DNS, but a failing check is what to raise with an
    // administrator. Same report the domain admin sees.
    deliverability: () =>
      client.apiFetch<{
        domain: string;
        grade?: string;
        summary?: { pass?: number; warn?: number; fail?: number };
        checks?: { key?: string; label?: string; status?: string; detail?: string }[] | null;
      }>("/mail/me/deliverability"),

    // ── Held mail ───────────────────────────────────────────────────────────────────────
    // incomplete: a mail server's held mail could not be read just now (mail/queuesnapshot.go).
    quarantine: () => client.apiFetch<{ items: HeldMessage[] | null; total: number; incomplete?: string }>("/mail/me/quarantine"),
    releaseHeld: (id: number | string) => post<unknown>(`/mail/me/quarantine/${encodeURIComponent(String(id))}/release`),
    deleteHeld: (id: number | string) => del<unknown>(`/mail/me/quarantine/${encodeURIComponent(String(id))}`),

    // ── App passwords and devices ───────────────────────────────────────────────────────
    // `max` and `note` were both being dropped. The cap is real — Dovecot's passdb needs one
    // chained block per slot — so a create at the limit is refused, and a Create button that is
    // always enabled turns that into an error the user cannot predict.
    appPasswords: () =>
      client.apiFetch<{ items: AppPassword[] | null; total: number; max?: number; note?: string }>(
        "/mail/me/app-passwords",
      ),
    createAppPassword: (name: string) =>
      post<{ password?: string; username?: string; device_tag?: string; note?: string }>(
        "/mail/me/app-passwords",
        { name },
      ),
    deleteAppPassword: (id: number) => del<unknown>(`/mail/me/app-passwords/${id}`),
    devices: () => client.apiFetch<{ items: Device[] | null; total: number }>("/mail/me/devices"),

    // ── Push notifications ──────────────────────────────────────────────────────────────
    // The server sends `public_key`. This declared `key`, so the destructure produced undefined
    // and the conversion to a Uint8Array threw "cannot read properties of undefined" — the
    // subscribe button failed on its first line with an error naming nothing recognisable.
    vapidKey: () => client.apiFetch<{ public_key: string }>("/mail/push/vapid-key"),
    pushSubscriptions: () =>
      // The server answers under `subscriptions`. This read `items`/`total`, which it does not
      // send, so the device list was permanently empty — a user who HAD subscribed was told
      // they had not, and had no way to revoke a device.
      client.apiFetch<{ subscriptions: PushSubscription[] | null }>("/mail/me/push/subscriptions"),
    subscribePush: (endpoint: string, keys: unknown, user_agent: string) =>
      post<unknown>("/mail/me/push/subscriptions", { endpoint, keys, user_agent }),
    unsubscribePush: (id: number) => del<unknown>(`/mail/me/push/subscriptions/${id}`),
    testPush: () => post<unknown>("/mail/me/push/test"),

    // ── Panel account: password, sessions, 2FA, API keys ────────────────────────────────
    setPanelPassword: (old_password: string, new_password: string) =>
      post<unknown>("/auth/password", { old_password, new_password }),
    sessions: () => client.apiFetch<{ items: Session[] | null; total: number }>("/auth/sessions"),
    revokeSession: (id: number | string) => del<unknown>(`/auth/sessions/${encodeURIComponent(String(id))}`),
    enroll2FA: () => post<{ secret?: string; otpauth_url?: string; qr?: string }>("/auth/2fa/enroll"),
    confirm2FA: (code: string) => post<unknown>("/auth/2fa/confirm", { code }),
    authTokens: () => client.apiFetch<{ items: ApiToken[] | null; total: number }>("/auth/tokens"),
    createAuthToken: (name: string) => post<{ token?: string }>("/auth/tokens", { name }),
    deleteAuthToken: (id: number) => del<unknown>(`/auth/tokens/${id}`),

    // ── Personal access tokens (/tokens/me) ─────────────────────────────────────────────
    // Answers under "tokens", not "items" — measured against the running node 2026-08-30. Read as
    // "items" the personal-token list was empty on every load while the endpoint returned them,
    // which reads as "you have no API tokens" rather than as a bug.
    myTokens: () => client.apiFetch<{ tokens: PersonalToken[] | null }>("/tokens/me"),
    // v1 posts {label} here and {name} to /auth/tokens. Two endpoints, two field names —
    // exactly the request/response shape drift CLAUDE.md keeps recording, so both are kept
    // as they actually are rather than unified into a lie.
    createMyToken: (label: string) => post<{ token?: string }>("/tokens/me", { label }),
    // Keyed on the JTI, which is what the route takes. It was called with `id`, a field
    // /tokens/me rows do not have, so every revoke posted `/tokens/me/undefined/revoke`.
    revokeMyToken: (jti: string) => post<unknown>(`/tokens/me/${encodeURIComponent(jti)}/revoke`),

    // ── Webmail ─────────────────────────────────────────────────────────────────────────
    // Confirmed against the running node 2026-08-30: {brand, clients, default, ozpanel,
    // redirect_seconds}. The speculative `items` alternative is gone — an untrue branch in a type
    // is worse than no branch, because it makes the real shape look uncertain and invites the next
    // reader to keep guessing.
    webmailClients: () => client.apiFetch<{ clients?: unknown[] }>("/webmail/clients"),
    // Where the FRAMED webmail lives — the ozpanel bar with the client inside it, served by
    // /app on the WEBMAIL host. It has to come from the server: a host-exposed domain mounts the
    // client under /<client>/ and a path-exposed one mounts it AT the prefix, so the frame is at
    // origin + "/app" in one shape and prefix + "/app" in the other. Every version of this that
    // reconstructed it in the browser was wrong for every path-exposed domain.
    // Carry this session to the WEBMAIL host, where the wrapper (and its top bar) is served.
    // The panel cannot cookie that host — different registrable domains, host-only cookie — so
    // the one-time link is the only way the wrapper can have a session to work with.
    webmailHandoff: (c?: string) =>
      post<{ url?: string; urls?: string[] }>(
        "/mail/me/webmail-handoff" + (c ? `?client=${encodeURIComponent(c)}` : ""),
        {},
      ),
    webmailApp: (c?: string) =>
      // `qr` is a PNG data URI of `url`, rendered by the server (internal/qr) so the address
      // never goes to a third-party QR service. It carries no token — see mail_sso.go.
      client.apiFetch<{ client: string; url?: string; urls?: string[]; qr?: string; note?: string }>(
        "/mail/me/webmail-app" + (c ? `?client=${encodeURIComponent(c)}` : ""),
      ),
    getWebmailDefault: () => client.apiFetch<WebmailDefault>("/mail/me/webmail-default"),
    setWebmailDefault: (c: WebmailClient) =>
      client.apiFetch<WebmailDefault>("/mail/me/webmail-default", {
        method: "PUT",
        body: JSON.stringify({ client: c }),
      }),
    getSound: () => client.apiFetch<SoundPref>("/mail/me/sound"),
    setSound: (theme: string) =>
      client.apiFetch<{ effective: string }>("/mail/me/sound", {
        method: "PUT",
        body: JSON.stringify({ theme }),
      }),
    mintSSO: (webmailClient: WebmailClient) =>
      client.apiFetch<SSOResult>(`/mail/me/sso/${webmailClient}`, { method: "POST" }),
  };
}
